import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Tape } from '@/pages/tapes/page';
import CreatableSelect from '@/components/CreatableSelect';
import { useOffice } from '@/hooks/useOffice';
import { logActivity } from '@/lib/activityLog';

interface Props {
  tape: Tape | null;
  onClose: () => void;
  onSaved: (newTape?: Tape) => void;
  allMediaSets?: string[];
  allBackupSets?: string[];
  allTapeTypes?: string[];
  allLocations?: string[];
  allBoxNumbers?: string[];
  defaultNeedsReplacement?: boolean;
}

export default function TapeEditModal({ tape, onClose, onSaved, allMediaSets = [], allBackupSets = [], allTapeTypes = [], allLocations = [], allBoxNumbers = [], defaultNeedsReplacement = false }: Props) {
  const { currentOfficeId, currentOffice } = useOffice();
  const isNew = !tape;
  const [form, setForm] = useState<Partial<Tape>>(tape || {
    media_label: '', media_set: '', backup_set: '', modified_date: '', tape_type: '', location: currentOffice?.name || '', box_number: '', notes: '', needs_replacement: defaultNeedsReplacement, damaged: false,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = <K extends keyof Tape>(k: K, v: any) => setForm((s) => ({ ...s, [k]: v }));

  const locationDefaultOptions = currentOffice?.name
    ? [currentOffice.name, 'Backup Tape Library']
    : ['Backup Tape Library'];

  const save = async () => {
    if (!form.media_label?.trim()) { setErr('Media Label is required'); return; }
    setSaving(true); setErr('');
    const payload = {
      media_label: form.media_label,
      media_set: form.media_set || null,
      backup_set: form.backup_set || null,
      modified_date: form.modified_date || null,
      tape_type: form.tape_type || null,
      location: form.location || null,
      box_number: form.box_number || null,
      notes: form.notes || null,
      needs_replacement: !!form.needs_replacement,
      damaged: !!form.damaged,
      updated_at: new Date().toISOString(),
    };
    if (isNew) {
      const { data, error } = await supabase
        .from('tapes')
        .insert({ ...payload, office_id: currentOfficeId, is_new: true })
        .select('*')
        .single();
      if (error) { setSaving(false); setErr(error.message); return; }

      // Sync: ensure the assigned box exists in the boxes table so the
      // Boxes page can show it (and its tape count) automatically.
      if (payload.box_number) {
        try {
          const { data: existingBox } = await supabase
            .from('boxes')
            .select('id')
            .eq('box_number', payload.box_number)
            .eq('office_id', currentOfficeId)
            .maybeSingle();
          if (!existingBox) {
            await supabase.from('boxes').insert({
              box_number: payload.box_number,
              location: payload.location || null,
              office_id: currentOfficeId,
            });
          }
        } catch (e) {
          // Box sync is best-effort; don't block the tape save on it.
        }
      }

      setSaving(false);
      onSaved(data as Tape);
      await logActivity('create_tape', `Created tape ${payload.media_label}`);
      return;
    } else {
      const { error } = await supabase.from('tapes').update(payload).eq('id', tape!.id);
      if (error) { setSaving(false); setErr(error.message); return; }
    }

    // Sync: ensure the assigned box exists in the boxes table so the
    // Boxes page can show it (and its tape count) automatically.
    if (payload.box_number) {
      try {
        const { data: existingBox } = await supabase
          .from('boxes')
          .select('id')
          .eq('box_number', payload.box_number)
          .eq('office_id', currentOfficeId)
          .maybeSingle();
        if (!existingBox) {
          await supabase.from('boxes').insert({
            box_number: payload.box_number,
            location: payload.location || null,
            office_id: currentOfficeId,
          });
        }
      } catch (e) {
        // Box sync is best-effort; don't block the tape save on it.
      }
    }

    setSaving(false);
    onSaved();
    await logActivity('update_tape', `Updated tape ${payload.media_label}`);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-sm">{isNew ? 'New Tape' : `Edit Tape · ${tape?.media_label}`}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 cursor-pointer">
            <i className="ri-close-line w-5 h-5 flex items-center justify-center" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Media Label *</label>
            <input
              value={form.media_label || ''}
              onChange={(e) => set('media_label', e.target.value)}
              placeholder="e.g. HC8591"
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Media Set</label>
            <CreatableSelect
              value={form.media_set || ''}
              onChange={(v) => set('media_set', v)}
              options={allMediaSets}
              defaultOptions={['Monthly Full Backup', 'Weekly Full Backup', 'Daily Backup', 'Yearly Backup', 'CTU Project Backup', 'SHA Project Backup']}
              placeholder="Select or add Media Set"
              clearable
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Backup Set</label>
            <CreatableSelect
              value={form.backup_set || ''}
              onChange={(v) => set('backup_set', v)}
              options={allBackupSets}
              defaultOptions={[]}
              placeholder="Select or add Backup Set"
              clearable
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Tape Type</label>
            <CreatableSelect
              value={form.tape_type || ''}
              onChange={(v) => set('tape_type', v)}
              options={allTapeTypes}
              defaultOptions={['Daily', 'Weekly', 'Monthly', 'Yearly']}
              placeholder="Select or add Tape Type"
              clearable
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Location</label>
            <CreatableSelect
              value={form.location || ''}
              onChange={(v) => set('location', v)}
              options={allLocations}
              defaultOptions={locationDefaultOptions}
              placeholder="Select or add Location"
              clearable
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Modified Date</label>
            <input
              type="date"
              value={(() => {
                const val = form.modified_date || '';
                if (!val) return '';
                const parts = val.split(/[-\/\.]/);
                if (parts.length === 3) {
                  const [a, b, c] = parts;
                  if (a.length === 4) return `${a}-${b.padStart(2, '0')}-${c.padStart(2, '0')}`;
                  const yy = c.length === 2 ? `20${c}` : c;
                  return `${yy}-${a.padStart(2, '0')}-${b.padStart(2, '0')}`;
                }
                return val.split(' ')[0];
              })()}
              onChange={(e) => {
                const val = e.target.value;
                set('modified_date', val);
              }}
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Box Number</label>
            <CreatableSelect
              value={form.box_number || ''}
              onChange={(v) => set('box_number', v)}
              options={allBoxNumbers}
              defaultOptions={['Backup Tape Library']}
              placeholder="Select or add Box Number"
              clearable
              emptyLabel="(None)"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Notes</label>
            <textarea
              value={form.notes || ''} onChange={(e) => set('notes', e.target.value)}
              rows={3}
              className="w-full text-sm px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-slate-900 resize-none"
            />
          </div>
          <label className="flex items-center gap-2.5 py-1 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!form.needs_replacement}
              onChange={(e) => set('needs_replacement', e.target.checked)}
              className="w-4 h-4 cursor-pointer accent-rose-600"
            />
            <span className="text-sm text-slate-700">Needs replacement</span>
          </label>
          <label className="flex items-center gap-2.5 py-1 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!form.damaged}
              onChange={(e) => set('damaged', e.target.checked)}
              className="w-4 h-4 cursor-pointer accent-red-600"
            />
            <span className="text-sm text-slate-700">Damaged</span>
          </label>
          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-200 cursor-pointer whitespace-nowrap">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-md bg-slate-900 text-white cursor-pointer whitespace-nowrap disabled:opacity-60">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}